import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_vulcan-talent/artifacts/0qmfv81b_ChatGPT%20Image%20Mar%2015%2C%202026%2C%2007_35_42%20PM.png";

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/employers', label: 'Employers' },
  { href: '/candidates', label: 'Candidates' },
  { href: '/sectors', label: 'Sectors' },
  { href: '/jobs', label: 'Jobs' },
  { href: '/contact', label: 'Contact' },
];

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg'
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Vulcan Recruitment" 
              className="h-14 md:h-20 w-auto"
            />
          </Link>

          {/* Right side - Phone, CTA and Menu */}
          <div className="flex items-center gap-3 md:gap-6">
            {/* Phone - hidden on mobile */}
            <a 
              href="tel:+61469362319" 
              className="hidden md:flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-[#1A8B9D] transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              +61 469 362 319
            </a>
            
            {/* CTA Button */}
            <Link to="/employers#request-staff" className="hidden sm:block">
              <Button 
                data-testid="header-cta"
                className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide px-4 md:px-6 text-sm"
              >
                Request Staff
              </Button>
            </Link>

            {/* Hamburger Menu */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="outline" 
                  size="icon"
                  data-testid="menu-toggle"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 h-10 w-10"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] bg-white p-0">
                <div className="flex flex-col h-full">
                  {/* Menu Header */}
                  <div className="p-6 border-b border-slate-200">
                    <img 
                      src={LOGO_URL} 
                      alt="Vulcan Recruitment" 
                      className="h-14 w-auto"
                    />
                  </div>
                  
                  {/* Navigation Links */}
                  <nav className="flex-1 py-4" data-testid="sidebar-nav">
                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setMenuOpen(false)}
                        data-testid={`nav-${link.label.toLowerCase()}`}
                        className={`block py-4 px-6 text-base font-semibold uppercase tracking-wide border-l-4 transition-all ${
                          isActive(link.href)
                            ? 'text-[#1A8B9D] bg-teal-50 border-[#1A8B9D]'
                            : 'text-slate-700 hover:text-[#1A8B9D] hover:bg-slate-50 border-transparent'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </nav>
                  
                  {/* Menu Footer */}
                  <div className="p-6 bg-slate-50 border-t border-slate-200">
                    <div className="space-y-4 mb-6">
                      <a 
                        href="tel:+61469362319" 
                        className="flex items-center gap-3 text-slate-700 hover:text-[#1A8B9D] transition-colors"
                      >
                        <Phone className="w-5 h-5 text-[#F5A623]" />
                        <span className="font-semibold">+61 469 362 319</span>
                      </a>
                      <a 
                        href="mailto:info@vulcanrecruitment.com" 
                        className="flex items-center gap-3 text-slate-700 hover:text-[#1A8B9D] transition-colors"
                      >
                        <Mail className="w-5 h-5 text-[#F5A623]" />
                        <span className="font-semibold text-sm">info@vulcanrecruitment.com</span>
                      </a>
                    </div>
                    <Link to="/employers#request-staff" onClick={() => setMenuOpen(false)}>
                      <Button 
                        className="w-full bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-4 uppercase tracking-wide"
                      >
                        Request Staff
                      </Button>
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
